import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../util/api";
import { useLocation } from "react-router-dom";
import { notify } from "../../util/toast";
import { serverMsgOr } from "../../util/apiFeedback";

import SearchBar from "./SearchBar";
import BidDetailModal from "./BidDetailModal";
import FavoriteStar from "./components/FavoriteStar";
import { favoriteApi } from "./api/favoriteApi";

// MUI
import {
    Box,
    Typography,
    Stack,
    Divider,
    Backdrop,
    CircularProgress,
    Button,
    FormControlLabel,
    Checkbox,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableSortLabel,
    Card,
    CardContent,
    Chip,
} from "@mui/material";

import DownloadIcon from "@mui/icons-material/Download";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import { ds } from "../../styles/designSystem";
import LoadingDots from "../../component/common/LoadingDots";
import ActionButton from "../../component/common/ActionButton";
import ResizableTable from "../../component/common/ResizableTable";

// Charts (Recharts)
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Tooltip as ReTooltip,
} from "recharts";

const useQuery = () => new URLSearchParams(useLocation().search);

const formatWon = (n) =>
    Number.isFinite(Number(n)) ? `${Number(n).toLocaleString()}원` : "-";
const formatCount = (n) =>
    Number.isFinite(Number(n)) ? `${Number(n).toLocaleString()}건` : "-";
const formatPercent = (n) =>
    Number.isFinite(Number(n))
        ? `${Number(n).toLocaleString("ko-KR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
        : "-";

const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

/**
 * 낙찰자 칸 문구 — «없음(확정)» 과 «아직 확인 안 됨» 은 다르다.
 *
 * 서버는 실제로 식별된 낙찰자가 있을 때만 winningCompanyName 을 채우고, 없으면 null 과 함께
 * winningStatus 를 보낸다. 예전엔 DB 자리표시자("낙찰업체없음")가 그대로 내려와 두 경우가
 * 한 문구로 뭉개졌다 — 재추적 중인 건까지 «낙찰자가 없다» 고 단정해 보였다.
 */
const winnerLabel = (r) =>
    r?.winningCompanyName || (r?.winningStatus === "NO_WINNER" ? "낙찰자 없음" : "확인 중");

/**
 * 낙찰금액 칸 문구.
 *
 * UNDISCLOSED 는 조달청이 «앞으로도 안 준다» 는 뜻이라 기다릴 필요가 없다.
 * 그냥 비어 있는 것(아직 확인 전)과 구분해야 사용자가 다시 조회하지 않는다.
 */
const winningPriceLabel = (r) => {
    if (r?.winningPrice) return `${Number(r.winningPrice).toLocaleString()}원`;
    if (r?.winningPriceStatus === "UNDISCLOSED") return "비공개";
    if (r?.winningStatus === "NO_WINNER") return "-";
    return "확인 중";
};

/** 실제 값이 아닌 «상태» 문구는 업체명·금액처럼 보이지 않게 흐리게. */
const isPlaceholderText = (text) => text === "확인 중" || text === "비공개" || text === "낙찰자 없음";

/**
 * 서버가 준 실패 요약에서 앞머리의 자바 예외 클래스명을 떼어낸다.
 *
 * 백엔드(CompanySearchService.describe)는 실패 사유를 «예외타입: 메시지» 로 만든다.
 * 클래스명은 사용자에게 아무 의미가 없고, 무엇보다 뒤에 한국어 메시지가 붙으면
 * serverMsgOr 의 «한글이 있으면 사람 대상 문구» 판정을 통과해 그대로 노출된다.
 * 그래서 노출 직전에 접두어만 걷어낸다 — 남은 문구가 여전히 영문 기술 문자열이면
 * serverMsgOr 가 화면 폴백으로 바꿔준다.
 */
const stripExceptionType = (msg) =>
    typeof msg === "string"
        ? msg.replace(/^[A-Za-z_$][\w$.]*(?:Exception|Error|Throwable)\s*:\s*/, "")
        : "";

const cardSx = {
    ...ds.components.external.card,
};

const sectionTitleSx = { ...ds.typography.sectionTitle, fontSize: 18 };

// ✅ 색상 (요청 반영)
const COLORS = {
    winRate: "#22c55e", // 낙찰률(초록)
    winAmount: "#3182f6", // 낙찰금액(파랑)

    // 다양하게
    winCount: "#3b82f6", // 낙찰 건수
    totalBid: "#06b6d4", // 투찰(민트)
    muted: ds.colors.border,
};

// ✅ 커스텀 Tooltip: 위치 튐 방지 + 디자인 정리
function CustomTooltip({ active, payload }) {
    if (!active || !payload?.length) return null;

    const p = payload[0];
    return (
        <Box
            sx={{
                bgcolor: ds.colors.surface,
                border: `1px solid ${ds.colors.border}`,
                boxShadow: "0 12px 30px rgba(15, 23, 42, 0.12)",
                borderRadius: 1.5,
                px: 1.25,
                py: 0.75,
            }}
            width={100}
        >
            <Typography sx={{ fontSize: 12, fontWeight: 900, mb: 0.25 }}>
                {p.name}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                {p.valueText ?? p.value?.toLocaleString()}
            </Typography>
        </Box>
    );
}

function DonutCard({
                                      title,
                                      subtitle,
                                      mainLabel,
                                      mainValueText,
                                      data,
                                      colors,
                                  }) {
    return (
        <Box
            sx={{
                ...ds.components.external.card,
                p: 2,
                height: 300,            // ✅ 높이 고정 (차트 계산 안정화)
                overflow: "hidden",      // ✅ 밖으로 나가는 것 컷
            }}
        >
            <Stack spacing={0.25} sx={{ mb: 1 }}>
                <Typography sx={ds.typography.sectionTitle}>{title}</Typography>
                <Typography variant="caption" color="text.secondary">
                    {subtitle}
                </Typography>
            </Stack>

            <Box sx={{ position: "relative", height: 220 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={data}
                            dataKey="value"
                            nameKey="name"
                            // ✅ 중앙 고정 (튀는 현상 방지)
                            // ✅ 반지름 축소해서 카드 밖으로 안 나가게
                            innerRadius={80}
                            outerRadius={110}
                            startAngle={90}
                            endAngle={-270}
                            paddingAngle={2}
                            isAnimationActive={true} // ✅ 툴팁/레이아웃 튐 줄이기
                        >
                            {data.map((_, idx) => (
                                <Cell key={idx} fill={colors[idx]} />
                            ))}
                        </Pie>

                        <ReTooltip
                            content={<CustomTooltip />}
                            // ✅ 툴팁 튐 방지 옵션
                            wrapperStyle={{ pointerEvents: "none" }}
                            position={{ x: 12, y: 12 }} // ✅ 카드 내부 좌상단 고정(가장 안정적)
                            allowEscapeViewBox={{ x: false, y: false }}
                            isAnimationActive={false}
                        />
                    </PieChart>
                </ResponsiveContainer>

                {/* center label */}
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        pointerEvents: "none",
                        textAlign: "center"
                    }}
                >

                    <Typography variant="caption" color="text.secondary">
                        {mainLabel}
                    </Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: 16, letterSpacing: "-0.02em" }}>
                        {mainValueText}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}

function MiniStat({ title, value, accent }) {
    return (
        <Box
            sx={{
                ...ds.components.external.card,
                p: 1.5,
                textAlign: "left",
            }}
        >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                {title}
            </Typography>
            <Typography
                sx={{
                    mt: 0.5,
                    fontWeight: 800,
                    fontSize: 18,
                    letterSpacing: "-0.02em",
                    color: accent || "text.primary",
                }}
            >
                {value}
            </Typography>
        </Box>
    );
}

const CompanySearchPage = () => {
    const query = useQuery();
    const bizNumber = query.get("bizNumber");

    const [company, setCompany] = useState(null);

    // 즐겨찾기 — 회사가 set 되면 별 상태 조회. 토글은 사업자번호 기준.
    const [companyFav, setCompanyFav] = useState(false);
    useEffect(() => {
        const biz = company?.businessNumber;
        if (!biz) { setCompanyFav(false); return; }
        let cancelled = false;
        (async () => {
            try {
                const favs = await favoriteApi.filterCompanyBiznos([biz]);
                if (!cancelled) setCompanyFav(Array.isArray(favs) ? favs.includes(biz) : !!favs?.[biz]);
            } catch { /* silent */ }
        })();
        return () => { cancelled = true; };
    }, [company?.businessNumber]);
    const toggleCompanyFav = async (next) => {
        const biz = company?.businessNumber;
        if (!biz) return;
        setCompanyFav(next);   // 낙관적
        try {
            if (next) await favoriteApi.addCompany(biz);
            else      await favoriteApi.removeCompany(biz);
        } catch { setCompanyFav(!next); /* 롤백 */ }
    };
    const [stats, setStats] = useState(null);
    const [progress, setProgress] = useState(null);
    // 수집 작업이 «실패로» 끝난 사유. null 이 아니면 진행률 대신 실패 안내 + 재시도를 보여준다.
    const [taskError, setTaskError] = useState(null);
    const [selected, setSelected] = useState(null);
    const [showOnlyWon, setShowOnlyWon] = useState(false);

    // 정렬
    const [sortKey, setSortKey] = useState("bidOpenDate");
    const [sortAsc, setSortAsc] = useState(false);

    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (bizNumber) handleSearch(bizNumber);
        // eslint-disable-next-line
    }, [bizNumber]);

    const handleSearch = async (businessNumber) => {
        setLoading(true);
        setStats(null);
        setCompany(null);
        setProgress(null);
        setTaskError(null);

        try {
            // 1) 통계 + 목록
            const res = await api.get(
                `${process.env.REACT_APP_API_BASE_URL}/companies/bidding-results`,
                { bizNumber: businessNumber }
            );

            if (res?.working || res?.status === 202) {
                const token = localStorage.getItem("auth_token");

                const es = new EventSource(
                    `${process.env.REACT_APP_API_BASE_URL}/companies/bidding-results/stream?bizNumber=${encodeURIComponent(
                        businessNumber
                    )}&token=${encodeURIComponent(token || "")}`
                );

                es.addEventListener("progress", (e) => {
                    try {
                        setProgress(JSON.parse(e.data));
                    } catch {
                        // ignore
                    }
                });

                es.addEventListener("done", async () => {
                    es.close();
                    setProgress(null);

                    const statsRes = await api.get(
                        `${process.env.REACT_APP_API_BASE_URL}/companies/bidding-results`,
                        { bizNumber: businessNumber }
                    );

                    /**
                     * ⚠️ 수집이 끝났는데 서버가 또 «작업 중» 이라고 하면 그건 정상이 아니다.
                     *
                     * 서버의 미개찰 판정이 자기가 방금 쓴 값을 다시 «미수집» 으로 보면
                     * done → 재조회 → 202 → (백그라운드에서 같은 작업 재시작) 이 끝없이 돈다.
                     * 실제로 그랬고, 이때 화면은 stats.allResults 가 없어서 목록 자리에
                     * 로딩만 계속 돌았다 — 사용자에겐 «영원히 로딩» 으로만 보이고 이유가 어디에도 없었다.
                     * 여기서 끊고 사유를 남긴다. 이 분기가 다시 타면 서버 회귀 신호다.
                     */
                    if (statsRes?.working || !statsRes?.allResults) {
                        const reason =
                            "수집은 끝났는데 서버가 아직 미완료로 봅니다. 잠시 후 다시 시도해 주세요.";
                        setTaskError(reason);
                        setLoading(false);
                        notify(reason, "warning");
                        return;
                    }

                    setStats(statsRes);
                });

                /**
                 * ⚠️ EventSource 는 서로 다른 두 가지를 «같은 error 핸들러» 로 던진다.
                 *
                 *  1) 서버가 보낸 `event: error` — 워커 스레드가 예외로 죽었다는 통지.
                 *     MessageEvent 로 오므로 e.data 에 JSON({company, message}) 이 들어 있다.
                 *  2) 전송 계층 오류 — 연결 끊김·서버 다운·인증 실패. 그냥 Event 라 e.data 가 없다.
                 *
                 * 구분 기준은 e.data 의 유무 하나뿐이다. 예전 코드는 `es.onerror = () => es.close()`
                 * 로 둘을 뭉뚱그렸고, 그래서 수집 실패가 «스트림만 조용히 닫힘» 으로 끝나
                 * 진행률 바가 영영 멈춘 채 남았다. 이유는 아무 데도 나오지 않았다.
                 * 이 분기를 지우면 그 버그가 그대로 돌아온다.
                 */
                es.addEventListener("error", (e) => {
                    if (!e.data) return; // 전송 계층 오류 → 아래 onerror 의 기존 동작(닫기)에 맡긴다

                    es.close();
                    console.error("❌ 낙찰 결과 수집 실패:", e.data);

                    let payload = null;
                    try {
                        payload = JSON.parse(e.data);
                    } catch {
                        // 문자열로 올 수도 있다 — 그 경우 e.data 자체가 사유다
                    }
                    const reason = serverMsgOr("낙찰 데이터 수집에 실패했습니다. 잠시 후 다시 시도해 주세요.")(
                        stripExceptionType(payload?.message ?? e.data)
                    );

                    // 진행률을 걷어내고(=«수집 중» 표시 해제) 실패 사유를 남긴다.
                    // Backdrop 도 내려야 «다시 시도» 를 누를 수 있다 — 실패는 회사 정보 조회보다
                    // 먼저 도착할 수 있어서 loading 이 true 인 채로 화면이 잠길 수 있다.
                    setProgress(null);
                    setTaskError(reason);
                    setLoading(false);
                    notify(reason, "error");
                });

                es.onerror = () => es.close();
            } else if (res?.allResults) {
                setStats(res);
            } else {
                setStats(null);
            }

            // 2) 회사 정보
            const companyInfo = await api.get(
                `${process.env.REACT_APP_API_BASE_URL}/companies/info`,
                { bizNumber: businessNumber }
            );
            setCompany(companyInfo);

            setLoading(false);
        } catch (err) {
            console.error("❌ 데이터 조회 실패:", err);
            setLoading(false);
            setStats(null);
            setCompany(null);
            setProgress(null);
        }
    };

    const handleSort = (key) => {
        if (sortKey === key) setSortAsc((v) => !v);
        else {
            setSortKey(key);
            setSortAsc(true);
        }
    };

    const displayResults = useMemo(() => {
        const base = showOnlyWon ? stats?.wonResults || [] : stats?.allResults || [];
        const arr = [...base];

        arr.sort((a, b) => {
            const av = a?.[sortKey];
            const bv = b?.[sortKey];
            if (av == null || bv == null) return 0;

            if (typeof av === "string" && typeof bv === "string") {
                return sortAsc ? av.localeCompare(bv) : bv.localeCompare(av);
            }
            return sortAsc ? Number(av) - Number(bv) : Number(bv) - Number(av);
        });

        return arr;
    }, [stats, showOnlyWon, sortKey, sortAsc]);

    const handleDownload = async () => {
        if (!bizNumber) return;
        setLoading(true);

        try {
            // api.download 는 axios 응답 객체를 준다 (http.js:19). .data 를 벗겨야 실제 Blob.
            const res  = await api.download(
                `${process.env.REACT_APP_API_BASE_URL}/companies/bidding-results/export`,
                { bizNumber }
            );
            const blob = res?.data ?? res;

            // 래핑은 유지 — 서버가 octet-stream 으로 내려주는 것을 xlsx MIME 으로 바꿔 붙이는 의도.
            const blobObj = new Blob([blob], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            const url = window.URL.createObjectURL(blobObj);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `bidding-results-${bizNumber}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("엑셀 다운로드 실패:", error);
        } finally {
            setLoading(false);
        }
    };

    // -------------------------
    // ✅ CHART DATA (요구 반영)
    // -------------------------
    const totalBidCount = Number(stats?.totalBidCount || 0);
    const totalWinCount = Number(stats?.totalWinCount || 0);
    const lostCount = Math.max(0, totalBidCount - totalWinCount);

    const totalBidAmount = Number(stats?.totalBidAmount || 0);
    const totalWinAmount = Number(stats?.totalWinAmount || 0);
    const lostAmount = Math.max(0, totalBidAmount - totalWinAmount);

    const winRate = clamp(Number(stats?.winRate || 0), 0, 100);

    // 1) 투찰/낙찰(건수) -> 낙찰 vs 미낙찰 (도넛)
    const bidWinCountDonut = useMemo(
        () => [
            { name: "낙찰", value: totalWinCount },
            { name: "미낙찰", value: lostCount },
        ],
        [totalWinCount, lostCount]
    );

    // 2) 금액 -> 낙찰금액 vs 미낙찰금액(=총투찰-낙찰) (도넛)
    const bidWinAmountDonut = useMemo(
        () => [
            { name: "낙찰금액", value: totalWinAmount },
            { name: "미낙찰금액", value: lostAmount },
        ],
        [totalWinAmount, lostAmount]
    );

    // 3) 낙찰률 -> 낙찰률 vs 나머지 (도넛)
    const winRateDonut = useMemo(
        () => [
            { name: "낙찰률", value: winRate },
            { name: "기타", value: Math.max(0, 100 - winRate) },
        ],
        [winRate]
    );

    return (
        <>
            {/* 전역 로딩 */}
            <Backdrop open={loading} sx={{ zIndex: (t) => t.zIndex.drawer + 1, bgcolor: "rgba(255,255,255,0.85)" }}>
                <Stack spacing={1} alignItems="center">
                    <LoadingDots label="자료를 불러오고 있어요" />
                </Stack>
            </Backdrop>

            {/* ✅ 회색 배경 제거 */}
            <Box sx={ds.components.external.page}>
                <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: "auto" }}>
                    <Stack spacing={2.5}>
                        {/* 검색 */}
                        <Card sx={cardSx}>
                            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                                <SearchBar
                                    onSelect={(bn) => {
                                        window.location.href = `/search?type=${encodeURIComponent(
                                            "기업 검색"
                                        )}&bizNumber=${encodeURIComponent(bn)}`;
                                    }}
                                />
                            </CardContent>
                        </Card>

                        {/* 사업자 정보 1카드 */}
                        <Card sx={cardSx}>
                            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                                {company ? (
                                    <Stack spacing={0.5}>
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            <Typography sx={ds.components.external.heroTitle}>
                                                {company.name}
                                            </Typography>
                                            {/* 기업 즐겨찾기 토글 — 사업자번호 키 */}
                                            <FavoriteStar
                                                checked={companyFav}
                                                onToggle={toggleCompanyFav}
                                                size="medium"
                                            />
                                        </Stack>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Typography variant="body2" color="text.secondary">
                                                사업자번호: {company.businessNumber}
                                            </Typography>
                                            <Chip
                                                size="small"
                                                label="기업"
                                                sx={{ ...ds.components.external.chip, height: 22 }}
                                            />
                                        </Stack>
                                    </Stack>
                                ) : (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <LoadingDots label="회사 정보를 불러오고 있어요" compact sx={{ py: 0 }} />
                                    </Stack>
                                )}
                            </CardContent>
                        </Card>

                        {/* 진행중 표시 */}
                        {progress && (
                            <Card sx={cardSx}>
                                <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                                    <Stack spacing={0.75}>
                                        <Typography sx={{ fontWeight: 900, fontSize: 16 }}>
                                            낙찰 데이터 수집 중... (페이지 이탈 후 다른 작업하고 오셔도 괜찮습니다)
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {progress.fetched}/{progress.total} 건 완료 (
                                            {formatPercent(progress.progressPercent || 0)})
                                        </Typography>
                                    </Stack>
                                </CardContent>
                            </Card>
                        )}

                        {/* 수집 실패 표시 — 멈춘 진행률 바 대신 «왜 실패했는지» 와 재시도 수단을 준다 */}
                        {taskError && (
                            <Card sx={cardSx}>
                                <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                                    <Stack
                                        direction={{ xs: "column", md: "row" }}
                                        spacing={1.5}
                                        alignItems={{ xs: "flex-start", md: "center" }}
                                        justifyContent="space-between"
                                    >
                                        <Stack spacing={0.75}>
                                            <Typography sx={{ fontWeight: 900, fontSize: 16, color: "#dc2626" }}>
                                                낙찰 데이터 수집에 실패했습니다
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {taskError}
                                            </Typography>
                                        </Stack>
                                        <ActionButton
                                            variant="refresh"
                                            label="다시 시도"
                                            disabled={loading || !bizNumber}
                                            onClick={() => handleSearch(bizNumber)}
                                        />
                                    </Stack>
                                </CardContent>
                            </Card>
                        )}

                        {/* 요약 + 3 도넛 차트 */}
                        <Card sx={cardSx}>
                            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                                <Stack spacing={1.5}>
                                    <Typography sx={sectionTitleSx}>요약</Typography>

                                    {/* 숫자 */}
                                    {stats ? (
                                        <Box
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(5, 1fr)" },
                                                gap: 1.5,
                                            }}
                                        >
                                            <MiniStat title="총 투찰" value={formatCount(totalBidCount)} />
                                            <MiniStat title="총 투찰금액" value={formatWon(totalBidAmount)} />
                                            <MiniStat title="낙찰" value={formatCount(totalWinCount)} accent={COLORS.winCount} />
                                            <MiniStat title="낙찰금액" value={formatWon(totalWinAmount)} accent={COLORS.winAmount} />
                                            <MiniStat title="낙찰률" value={formatPercent(winRate)} accent={COLORS.winRate} />
                                        </Box>
                                    ) : taskError ? null : !progress ? (
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <LoadingDots label="통계 정보를 불러오고 있어요" compact sx={{ py: 0 }} />
                                        </Stack>
                                    ) : null}

                                    <Divider />

                                    {/* ✅ 3 Donuts */}
                                    {stats && (
                                        <Box
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                                                gap: 2,
                                            }}
                                        >
                                            {/* 1) 투찰/낙찰(건수) */}
                                            <DonutCard
                                                title="투찰 / 낙찰 (건수)"
                                                subtitle="투찰 중 낙찰 건수 비중"
                                                mainLabel="낙찰 건수"
                                                mainValueText={formatCount(totalWinCount)}
                                                data={bidWinCountDonut}
                                                colors={[COLORS.winCount, COLORS.muted]}
                                                tooltipFormatter={(v, name) => [formatCount(v), name]}
                                            />

                                            {/* 2) 금액(투찰 중 낙찰액) */}
                                            <DonutCard
                                                title="총 투찰금액 중 낙찰금액"
                                                subtitle="총 투찰금액 대비 낙찰금액 비중"
                                                mainLabel="낙찰금액"
                                                mainValueText={formatWon(totalWinAmount)}
                                                data={bidWinAmountDonut}
                                                colors={[COLORS.winAmount, COLORS.muted]}
                                                tooltipFormatter={(v, name) => [formatWon(v), name]}
                                            />

                                            {/* 3) 낙찰률 */}
                                            <DonutCard
                                                title="낙찰률"
                                                subtitle="낙찰 비중(%)"
                                                mainLabel="낙찰률"
                                                mainValueText={formatPercent(winRate)}
                                                data={winRateDonut}
                                                colors={[COLORS.winRate, COLORS.muted]}
                                                tooltipFormatter={(v, name) => [formatPercent(v), name]}
                                            />
                                        </Box>
                                    )}
                                </Stack>
                            </CardContent>
                        </Card>

                        {/* 결과 목록 */}
                        <Card sx={cardSx}>
                            <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
                                <Stack
                                    direction={{ xs: "column", md: "row" }}
                                    spacing={1.5}
                                    alignItems={{ xs: "flex-start", md: "center" }}
                                    justifyContent="space-between"
                                    sx={{ mb: 1.5 }}
                                >
                                    <Box>
                                        <Typography sx={{ ...ds.typography.sectionTitle, fontSize: 18 }}>
                                            입찰 결과 목록
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            (낙찰 업체가 없는 건 낙찰 결과가 없는 것입니다)
                                        </Typography>
                                    </Box>

                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={showOnlyWon}
                                                    onChange={() => setShowOnlyWon((v) => !v)}
                                                />
                                            }
                                            label="낙찰된 공고만"
                                        />

                                        <ActionButton variant="download"
                                            onClick={handleDownload}
                                            disabled={!bizNumber || !stats?.allResults}
                                            label="결과 엑셀 다운로드" />
                                    </Stack>
                                </Stack>

                                <Divider sx={{ mb: 1.5 }} />

                                {stats?.allResults ? (
                                    <Box sx={{ width: "100%", overflowX: "auto" }}>
                                        <ResizableTable size="small">
                                            <TableHead>
                                                <TableRow sx={ds.components.tableHead}>
                                                    <TableCell sx={{ minWidth: 360, fontWeight: 900 }}>
                                                        <TableSortLabel
                                                            active={sortKey === "bidTitle"}
                                                            direction={sortAsc ? "asc" : "desc"}
                                                            onClick={() => handleSort("bidTitle")}
                                                        >
                                                            공고명
                                                        </TableSortLabel>
                                                    </TableCell>

                                                    <TableCell sx={{ minWidth: 120, fontWeight: 900 }}>
                                                        <TableSortLabel
                                                            active={sortKey === "bidOpenDate"}
                                                            direction={sortAsc ? "asc" : "desc"}
                                                            onClick={() => handleSort("bidOpenDate")}
                                                        >
                                                            개찰일
                                                        </TableSortLabel>
                                                    </TableCell>

                                                    <TableCell sx={{ minWidth: 200, fontWeight: 900 }}>
                                                        <TableSortLabel
                                                            active={sortKey === "winningCompanyName"}
                                                            direction={sortAsc ? "asc" : "desc"}
                                                            onClick={() => handleSort("winningCompanyName")}
                                                        >
                                                            낙찰업체
                                                        </TableSortLabel>
                                                    </TableCell>

                                                    <TableCell align="right" sx={{ minWidth: 160, fontWeight: 900 }}>
                                                        <TableSortLabel
                                                            active={sortKey === "winningPrice"}
                                                            direction={sortAsc ? "asc" : "desc"}
                                                            onClick={() => handleSort("winningPrice")}
                                                        >
                                                            낙찰금액
                                                        </TableSortLabel>
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>

                                            <TableBody>
                                                {displayResults.map((r, i) => (
                                                    <TableRow
                                                        key={i}
                                                        hover
                                                        sx={{
                                                            cursor: "pointer",
                                                            "&:hover": { bgcolor: ds.colors.primaryBg },
                                                        }}
                                                        onClick={() => setSelected(r)}
                                                    >
                                                        <TableCell>
                                                            <Typography variant="body2" noWrap sx={{ fontWeight: 700 }}>
                                                                {r.bidTitle}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            {r.bidOpenDate ? r.bidOpenDate.substring(0, 10) : "-"}
                                                        </TableCell>
                                                        <TableCell
                                                            sx={isPlaceholderText(winnerLabel(r))
                                                                ? { color: "text.disabled" } : undefined}
                                                        >
                                                            {winnerLabel(r)}
                                                        </TableCell>
                                                        <TableCell
                                                            align="right"
                                                            sx={isPlaceholderText(winningPriceLabel(r))
                                                                ? { color: "text.disabled" } : undefined}
                                                        >
                                                            {winningPriceLabel(r)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </ResizableTable>
                                    </Box>
                                ) : taskError ? null : !progress ? (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <LoadingDots label="입찰 결과 목록을 불러오고 있어요" compact sx={{ py: 0 }} />
                                    </Stack>
                                ) : (
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <HourglassTopIcon fontSize="small" color="action" />
                                        <Typography variant="body2" color="text.secondary">
                                            작업 진행 중입니다...
                                        </Typography>
                                    </Stack>
                                )}
                            </CardContent>
                        </Card>

                        {/* 상세 모달 */}
                        <BidDetailModal
                            bidNumber={selected?.bidNoticeNumber}
                            bidNoticeOrder={selected?.bidNoticeOrder}
                            isOpen={!!selected}
                            onClose={() => setSelected(null)}
                        />
                    </Stack>
                </Box>
            </Box>
        </>
    );
};

export default CompanySearchPage;
